import React, { useEffect, useState } from "react";
import api, { errMsg } from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Video, ShieldAlert, Clock, FileText, BellRing, Sparkles, Save, Loader2, DoorOpen,
  MessageSquareOff, CircleOff,
} from "lucide-react";

const Section = ({ icon: Icon, title, desc, children, testid }) => (
  <Card className="p-6 border-[#E6EDF2]" data-testid={testid} style={{ boxShadow: "var(--mmm-shadow-sm)" }}>
    <div className="flex items-center gap-3 mb-5">
      <span className="h-10 w-10 rounded-xl bg-[#1C4358]/8 text-[#1C4358] flex items-center justify-center shrink-0"><Icon size={19} /></span>
      <div>
        <div className="font-display font-semibold text-[#252221]">{title}</div>
        {desc && <div className="text-[13px] text-muted-foreground">{desc}</div>}
      </div>
    </div>
    <div className="space-y-4">{children}</div>
  </Card>
);

const Row = ({ label, hint, children }) => (
  <div className="flex items-start justify-between gap-4">
    <div className="min-w-0">
      <Label className="text-[14px] font-medium text-[#252221]">{label}</Label>
      {hint && <div className="text-[12px] text-muted-foreground mt-0.5">{hint}</div>}
    </div>
    <div className="shrink-0">{children}</div>
  </div>
);

export default function MeetingSettings() {
  const [s, setS] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/settings/meetings")
      .then(({ data }) => setS(data.settings))
      .catch((e) => toast.error(errMsg(e, "Could not load meeting settings.")))
      .finally(() => setLoading(false));
  }, []);

  const set = (k, v) => setS((p) => ({ ...p, [k]: v }));
  const setNum = (k, v) => set(k, v === "" ? "" : Number(v));

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        ...s,
        default_duration_minutes: Number(s.default_duration_minutes) || 45,
        guest_link_expiration_hours: Number(s.guest_link_expiration_hours) || 72,
        document_max_mb: Number(s.document_max_mb) || 25,
        reminder_hours_before: Number(s.reminder_hours_before) || 24,
        allowed_document_types: (Array.isArray(s.allowed_document_types)
          ? s.allowed_document_types
          : String(s.allowed_document_types || "").split(",")
        ).map((x) => x.trim().replace(/^\./, "").toLowerCase()).filter(Boolean),
      };
      const { data } = await api.put("/settings/meetings", payload);
      setS(data.settings);
      toast.success("Meeting settings saved.");
    } catch (e) { toast.error(errMsg(e, "Save failed.")); }
    finally { setSaving(false); }
  };

  if (loading) return <div className="max-w-3xl space-y-4"><Skeleton className="h-40" /><Skeleton className="h-40" /></div>;
  if (!s) return null;

  const docTypesStr = Array.isArray(s.allowed_document_types) ? s.allowed_document_types.join(", ") : (s.allowed_document_types || "");

  return (
    <div className="max-w-3xl space-y-6" data-testid="meeting-settings-page">
      <div>
        <h2 className="font-display text-2xl font-bold text-[#252221]">Meeting Settings</h2>
        <p className="text-sm text-muted-foreground mt-0.5">Video provider, guest access, documents, reminders, and AI controls for meetings.</p>
      </div>

      {/* Video provider */}
      <Section icon={Video} title="Video" desc="Meetings use Jitsi Meet through a provider abstraction." testid="settings-video-section">
        <Row label="Enable video meetings" hint="Turn off to hide the video join button everywhere.">
          <Switch checked={!!s.jitsi_enabled} onCheckedChange={(v) => set("jitsi_enabled", v)} data-testid="settings-jitsi-enabled" />
        </Row>
        <div>
          <Label className="text-[13px] text-[#4B5563]">Video provider</Label>
          <select value={s.video_provider || "jitsi"} onChange={(e) => set("video_provider", e.target.value)} data-testid="settings-video-provider"
            className="mt-1 w-full h-10 rounded-md border border-[#D6E0E7] text-[14px] px-2 bg-white">
            <option value="jitsi">Jitsi Meet</option>
          </select>
        </div>
        <div>
          <Label className="text-[13px] text-[#4B5563]">Jitsi domain</Label>
          <Input value={s.jitsi_domain || ""} onChange={(e) => set("jitsi_domain", e.target.value)} placeholder="meet.jit.si" className="h-10 text-[14px]" data-testid="settings-jitsi-domain" />
          <div className="text-[12px] text-muted-foreground mt-1">Use <b>meet.jit.si</b> (public) or your self-hosted Jitsi domain.</div>
        </div>
      </Section>

      {/* Guest access */}
      <Section icon={DoorOpen} title="Guest rooms & access" desc="Controls for temporary and professional guest links." testid="settings-guest-section">
        <Row label="Require a one-time access code" hint="Guests must enter a 6-digit code before joining a room.">
          <Switch checked={!!s.one_time_code_required} onCheckedChange={(v) => set("one_time_code_required", v)} data-testid="settings-one-time-code" />
        </Row>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label className="text-[13px] text-[#4B5563]">Guest link expires after (hours)</Label>
            <Input type="number" min="1" value={s.guest_link_expiration_hours ?? ""} onChange={(e) => setNum("guest_link_expiration_hours", e.target.value)} className="h-10 text-[14px]" data-testid="settings-guest-expiry" />
          </div>
          <div>
            <Label className="text-[13px] text-[#4B5563]">Default meeting duration (min)</Label>
            <Input type="number" min="5" value={s.default_duration_minutes ?? ""} onChange={(e) => setNum("default_duration_minutes", e.target.value)} className="h-10 text-[14px]" data-testid="settings-default-duration" />
          </div>
        </div>
        <Row label="Temporary guest continuation" hint="Allow temporary guests to keep a room and later convert to a client.">
          <Switch checked={!!s.guest_continuation_enabled} onCheckedChange={(v) => set("guest_continuation_enabled", v)} data-testid="settings-guest-continuation" />
        </Row>
        <Row label="Professional guest rooms" hint="Allow inviting professionals (attorney, agent, etc.) to scoped rooms.">
          <Switch checked={!!s.professional_guest_enabled} onCheckedChange={(v) => set("professional_guest_enabled", v)} data-testid="settings-professional-guest" />
        </Row>
        <Row label="External signing links" hint="Track an external e-signature link on agreements. No in-app signing.">
          <Switch checked={!!s.external_signing_enabled} onCheckedChange={(v) => set("external_signing_enabled", v)} data-testid="settings-external-signing" />
        </Row>
      </Section>

      {/* Documents */}
      <Section icon={FileText} title="Documents" desc="Limits for files shared in meeting rooms." testid="settings-documents-section">
        <div>
          <Label className="text-[13px] text-[#4B5563]">Maximum document size (MB)</Label>
          <Input type="number" min="1" value={s.document_max_mb ?? ""} onChange={(e) => setNum("document_max_mb", e.target.value)} className="h-10 text-[14px]" data-testid="settings-document-max-mb" />
        </div>
        <div>
          <Label className="text-[13px] text-[#4B5563]">Allowed document types</Label>
          <Input value={docTypesStr} onChange={(e) => set("allowed_document_types", e.target.value.split(","))} placeholder="pdf, jpg, png, docx" className="h-10 text-[14px]" data-testid="settings-allowed-doc-types" />
          <div className="text-[12px] text-muted-foreground mt-1">Comma-separated extensions (without the dot).</div>
        </div>
      </Section>

      {/* Reminders */}
      <Section icon={BellRing} title="Reminders" desc="In-app and email reminders before a meeting." testid="settings-reminders-section">
        <Row label="Send meeting reminders" hint="Notify the host before scheduled meetings.">
          <Switch checked={!!s.reminders_enabled} onCheckedChange={(v) => set("reminders_enabled", v)} data-testid="settings-reminders-enabled" />
        </Row>
        <div>
          <Label className="text-[13px] text-[#4B5563]">Remind this many hours before</Label>
          <Input type="number" min="1" value={s.reminder_hours_before ?? ""} onChange={(e) => setNum("reminder_hours_before", e.target.value)} className="h-10 text-[14px] max-w-[160px]" data-testid="settings-reminder-hours" />
        </div>
      </Section>

      {/* AI */}
      <Section icon={Sparkles} title="AI assistance" desc="AI output is always a staff-reviewed draft — it never updates the CRM or is shared without your approval." testid="settings-ai-section">
        <Row label="AI meeting summaries" hint="Let staff generate a draft summary after a meeting.">
          <Switch checked={!!s.ai_summary_enabled} onCheckedChange={(v) => set("ai_summary_enabled", v)} data-testid="settings-ai-summary" />
        </Row>
        <Row label="AI follow-up checklist drafts" hint="Include a draft follow-up checklist in AI summaries.">
          <Switch checked={!!s.ai_checklist_enabled} onCheckedChange={(v) => set("ai_checklist_enabled", v)} data-testid="settings-ai-checklist" />
        </Row>
      </Section>

      {/* Recording compliance */}
      <Section icon={ShieldAlert} title="Recording (compliance)" desc="Recording is OFF by default and requires legal/consent review." testid="settings-recording-section">
        <div className="flex items-start gap-2 rounded-lg bg-[#B7791F]/10 text-[#8a5d13] px-3 py-2.5 text-[13px]">
          <ShieldAlert size={16} className="mt-0.5 shrink-0" />
          Only enable recording after a legal and consent review, and after configuring recording at the provider level. Enabling this here does not fake or start any recording.
        </div>
        <Row label="Enable recording" hint="Keep OFF unless recording is fully configured and consented.">
          <Switch checked={!!s.recording_enabled} onCheckedChange={(v) => set("recording_enabled", v)} data-testid="settings-recording-enabled" />
        </Row>
        <div>
          <Label className="text-[13px] text-[#4B5563]">Recording compliance note</Label>
          <Input value={s.recording_compliance_note || ""} onChange={(e) => set("recording_compliance_note", e.target.value)} className="h-10 text-[14px]" data-testid="settings-recording-note" />
        </div>
      </Section>

      {/* SMS (disabled) */}
      <Section icon={MessageSquareOff} title="SMS notifications" desc="SMS is disabled platform-wide until compliance is confirmed." testid="settings-sms-section">
        <div className="flex items-center justify-between gap-4 rounded-lg border border-dashed border-[#D6E0E7] bg-[#F1F5F8] px-3 py-3">
          <div className="flex items-center gap-2 text-[13px] text-[#4B5563]">
            <CircleOff size={16} className="text-[#9AA5B1]" /> SMS notifications are turned off. In-app and email notifications remain active.
          </div>
          <Switch checked={false} disabled data-testid="settings-sms-enabled" />
        </div>
      </Section>

      <div className="flex justify-end pb-8">
        <Button onClick={save} disabled={saving} className="bg-[#9C1E22] hover:bg-[#861A1D] text-white gap-1.5 h-11 px-6 rounded-full" data-testid="settings-meetings-save-btn">
          {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />} Save settings
        </Button>
      </div>
    </div>
  );
}
